import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SupportButton from "@/components/base/SupportButton";
import { CheckCircle, Mail, Phone, MapPin } from "lucide-react";

const FORM_URL = import.meta.env.VITE_CONTACT_FORM_URL || "https://readdy.ai/api/form/d85l92g8j4jf7rnjoarg";

export default function ContactoPage() {
  const [searchParams] = useSearchParams();
  const defaultTipo = searchParams.get("tipo_consulta") || "";
  const [darkMode, setDarkMode] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Hubo un problema al enviar. Por favor intenta de nuevo.");
      }
    } catch {
      setError("Error de conexión. Verifica tu internet e intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className={`min-h-screen ${darkMode ? "bg-slate-950 text-white" : "bg-white text-slate-900"}`}>
        <Navbar darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} />

        <main className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-14">
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-slate-200 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">
                Contacto Enterprise
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                Habla con nuestro equipo
              </h1>
              <p className="text-slate-500 text-base max-w-md mx-auto">
                Cuéntanos sobre tu empresa y tus necesidades de facturación electrónica. Respondemos en menos de 4 horas.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              {/* Left: info */}
              <aside className="lg:col-span-2 space-y-6">
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest mb-4">Información de contacto</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 flex-shrink-0">
                        <Mail size={15} className="text-blue-800" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Correo (soporte, DGII y operaciones ECF)</p>
                        <a href="mailto:ecf@codec.do" className="text-sm font-medium text-slate-800 hover:text-blue-800 cursor-pointer">
                          ecf@codec.do
                        </a>
                        <p className="text-xs text-slate-400 mt-2">Ventas</p>
                        <a href="mailto:ventas@codec.do" className="text-sm font-medium text-slate-800 hover:text-blue-800 cursor-pointer">
                          ventas@codec.do
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 flex-shrink-0">
                        <Phone size={15} className="text-blue-800" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Teléfono</p>
                        <a href="tel:+18294570964" className="text-sm font-medium text-slate-800 hover:text-blue-800 cursor-pointer">
                          +1 (829) 457-0964
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-slate-200 flex-shrink-0">
                        <MapPin size={15} className="text-blue-800" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Ubicación</p>
                        <p className="text-sm font-medium text-slate-800">
                          Calle 2-A #12-B, Edif. Asset Enterprise, 3er Nivel, El Millón, DN
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-800 rounded-2xl p-6 text-white">
                  <h3 className="font-bold text-base mb-2">¿Listo para empezar?</h3>
                  <p className="text-blue-200 text-sm mb-4">
                    Registra tu empresa y obtén tu API Key en minutos. Sin tarjeta de crédito.
                  </p>
                  <a
                    href="/signup"
                    className="inline-block bg-white text-blue-800 font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    Registrar empresa gratis
                  </a>
                </div>

                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Por qué elegirnos</h3>
                  {[
                    "Certificado DGII para todos los tipos e-CF",
                    "Integración con Odoo, SAP, Dynamics en horas",
                    "SLA 99.95% con soporte 24/7",
                    "Firma digital con P12 cifrado",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2">
                      <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
              </aside>

              {/* Right: form */}
              <div className="lg:col-span-3">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
                      <CheckCircle size={28} className="text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">¡Mensaje recibido!</h3>
                    <p className="text-slate-500 text-sm max-w-sm">
                      Te responderemos al correo registrado en menos de 4 horas en días hábiles.
                    </p>
                  </div>
                ) : (
                  <form

                    onSubmit={handleSubmit}
                    className="bg-white rounded-2xl border border-slate-100 p-8 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Nombre completo</label>
                        <input
                          type="text"
                          name="nombre"
                          required
                          placeholder="Juan Pérez"
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Correo electrónico</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="juan@empresa.com"
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Empresa</label>
                        <input
                          type="text"
                          name="empresa"
                          required
                          placeholder="Empresa Ejemplo, S.R.L."
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">RNC</label>
                        <input
                          type="text"
                          name="rnc"
                          placeholder="1-30-52765-3"
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Teléfono</label>
                        <input
                          type="tel"
                          name="telefono"
                          placeholder="+1 (809) 555-0001"
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">ERP que usas</label>
                        <select
                          name="erp"
                          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors cursor-pointer"
                        >
                          <option value="">Seleccionar...</option>
                          {["Odoo", "SAP", "Dynamics 365", "NetSuite", "Oracle", "Propio"].map((e) => (
                            <option key={e} value={e}>{e}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Tipo de consulta</label>
                      <select
                        name="tipo_consulta"
                        required
                        defaultValue={defaultTipo}
                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors cursor-pointer"
                      >
                        <option value="">Seleccionar tipo...</option>
                        <option value="demo">Solicitar demo</option>
                        <option value="cotizacion">Cotización Enterprise</option>
                        <option value="firma_contable">Plan Firma Contable</option>
                        <option value="integracion">Consulta de integración</option>
                        <option value="soporte">Soporte técnico</option>
                        <option value="partnership">Partnership / Reseller</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Mensaje</label>
                      <textarea
                        name="mensaje"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="Cuéntanos sobre tu empresa, el volumen de comprobantes que emites y qué necesitas..."
                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors resize-none"
                      />
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-800 text-white font-semibold py-3 rounded-xl hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? "Enviando..." : "Enviar mensaje"}
                    </button>

                    <p className="text-xs text-slate-400 text-center">
                      Al enviar aceptas nuestra{" "}
                      <a href="/privacy" className="underline cursor-pointer">
                        Política de privacidad
                      </a>
                      . Soporte:{" "}
                      <a href="mailto:ecf@codec.do" className="underline cursor-pointer">
                        ecf@codec.do
                      </a>
                      . Primera respuesta orientativa &lt; 4 horas hábiles (P2/P3).
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </main>

        <Footer />
        <SupportButton />
      </div>
    </div>
  );
}